import React from "react"
import {
	getItemStatus,
	getUpdatesCount,
	getWarningsCount,
	orderStatuses,
	OrderTimelineDetail,
} from "../../services/order/eventHelper"
import {EventResponse} from "../../dtos/eventResponse"
import {Box, Chip, Tooltip, Typography} from "@mui/material"
import {
	Timeline,
	TimelineConnector,
	TimelineContent,
	TimelineDot,
	TimelineItem,
	TimelineSeparator,
} from "@mui/lab"
import WarningIcon from "@mui/icons-material/Warning"
import InfoIcon from "@mui/icons-material/Info"
import {makeStyles} from "tss-react/mui"

interface OrderTimelineProps {
	orderCreationDate: string
	orderTimelineDetails: OrderTimelineDetail[]
	isOrderPaid: boolean | undefined
	eventResponse: EventResponse | null
}
export const useStyles = makeStyles()({
	container: {
		display: "flex",
		width: "360px",
		padding: "8px",
		flexDirection: "column",
		alignItems: "flex-start",
		background: "var(--primary-states-hover, rgba(33, 150, 243, 0.04))",
	},
	timelineDotHighlighted: {
		background: "var(--text-primary, rgba(0, 0, 0, 0.87))",
		marginBottom: "7px",
	},
	timelineDot: {
		strokeWidth: "2px",
		stroke: "var(--action-disabled, rgba(0, 0, 0, 0.38))",
		marginBottom: "7px",
	},
	timelineConnectorHighlighted: {
		width: "12px",
		height: "2px",
		background: "var(--text-primary, rgba(0, 0, 0, 0.87))",
		transform: "rotate(90deg)",
	},
	timelineConnector: {
		width: "12px",
		height: "2px",
		background: "var(--action-disabled, rgba(0, 0, 0, 0.38))",
		transform: "rotate(90deg)",
	},
	timeline: {
		paddingLeft: "0px",
		margin: "0px",
	},
	timelineItem: {
		display: "flex",
		width: "330px",
		height: "34px",
		minHeight: "34px",
		alignItems: "flex-start",
		gap: "8px",
		paddingLeft: "0px",
		"&:before": {
			padding: "0px",
		},
	},
	timeLineSeparator: {
		display: "flex",
		alignItems: "center",
		flex: 1,
	},
	statusHighlighted: {
		display: "table",
		alignItems: "baseline",
		color: "var(--text-primary, rgba(0, 0, 0, 0.87))",
		fontFeatureSettings: "'liga' off, 'clig' off",
		fontSize: "12px",
		fontStyle: "normal",
		lineHeight: "166%",
		letterSpacing: "0.4px",
		width: "303px",
		fontWeight: 700,
	},
	status: {
		display: "table",
		alignItems: "baseline",
		color: "var(--text-primary, rgba(0, 0, 0, 0.38))",
		fontFeatureSettings: "'liga' off, 'clig' off",
		fontSize: "12px",
		fontStyle: "normal",
		lineHeight: "166%",
		letterSpacing: "0.4px",
		width: "303px",
		fontWeight: 700,
	},
	statusesContainer: {
		display: "flex",
		width: "150px",
		alignItems: "center",
		gap: "8px",
	},
	warningsContainer: {
		display: "flex",
		alignItems: "center",
		gap: "8px",
	},
	detail: {
		display: "flex",
		color: "var(--text-primary, rgba(0, 0, 0, 0.87))",
		fontFeatureSettings: "'liga' off, 'clig' off",
		fontSize: "12px",
		fontStyle: "normal",
		lineHeight: "166%",
		letterSpacing: "0.4px",
		width: "max-content",
	},
	warningIcon: {
		color: "#ED6C02",
		width: "20px",
		height: "20px",
	},
	infoIcon: {
		color: "#03A9F4",
		width: "20px",
		height: "20px",
	},
	chip: {
		height: "24px",
		background: "#db1002",
		color: "white",
		borderRadius: "100px",
		".MuiChip-label": {
			paddingLeft: "6px",
			paddingRight: "6px",
			fontFeatureSettings: "'liga' off, 'clig' off",
			fontSize: "13px",
			fontStyle: "normal",
			fontWeight: 400,
			lineHeight: "18px",
			letterSpacing: "0.16px",
		},
	},
})

export default function OrderTimeline(props: OrderTimelineProps) {
	const {classes} = useStyles()
	const getStatusItemsNumber = (status: string) => {
		const filteredTimeLines = props.orderTimelineDetails.filter(
			(timeLineItem) => timeLineItem.status === status
		)
		if (filteredTimeLines.length > 0) {
			return filteredTimeLines[0].itemsNumber ? filteredTimeLines[0].itemsNumber : 0
		}
		return 0
	}

	const getOrderStatus = () => {
		if (props.orderTimelineDetails.length > 0) {
			const deliveredItemsCount = getStatusItemsNumber("Order Delivered")
			if (deliveredItemsCount > 0) {
				return "Order Delivered"
			} else {
				const shippedItemsCount = getStatusItemsNumber("Shipment started")
				if (shippedItemsCount > 0) {
					return "Shipment started"
				} else {
					const productionStartedItemsCount = getStatusItemsNumber("Production started")
					if (productionStartedItemsCount > 0) {
						return "Production started"
					} else {
						return props.isOrderPaid ? "Order paid" : "Order created"
					}
				}
			}
		}
		return "Order created"
	}
	const orderStatus = getOrderStatus()
	const displayOrderCreatedStatus = () => {
		return (
			<Typography className={classes.statusHighlighted}>
				{"Order created "}
				{<span style={{fontWeight: 400}}>{props.orderCreationDate}</span>}
			</Typography>
		)
	}
	const isStatusHighlighted = (timeLineItemDetail: OrderTimelineDetail) => {
		return orderStatuses.indexOf(timeLineItemDetail.status) <= orderStatuses.indexOf(orderStatus)
	}

	const displayOrderTimeLineItem = (timeLineItemDetail: OrderTimelineDetail) => {
		return (
			<Typography
				className={
					isStatusHighlighted(timeLineItemDetail) ? classes.statusHighlighted : classes.status
				}
			>
				{timeLineItemDetail.status}{" "}
				{timeLineItemDetail.status !== "Order paid" && (
					<span style={{fontWeight: 400}}>
						{isStatusHighlighted(timeLineItemDetail) &&
						timeLineItemDetail.itemsNumber &&
						timeLineItemDetail.itemsNumber !== 0
							? `for ${
									timeLineItemDetail.itemsNumber === 1 ? "one" : timeLineItemDetail.itemsNumber
							  } item${
									timeLineItemDetail.itemsNumber && timeLineItemDetail.itemsNumber > 1 ? "s" : ""
							  }`
							: "for one or more items"}
					</span>
				)}
			</Typography>
		)
	}
	const getCancelledItemsCount = () => {
		if (
			props.eventResponse &&
			props.eventResponse.itemEvents &&
			props.eventResponse.itemEvents.length > 0
		) {
			const cancelledItems = props.eventResponse.itemEvents.filter(
				(item) => getItemStatus(item.events.map((keyEvent) => keyEvent.event)) === "Cancelled"
			)
			return cancelledItems.length
		}
		return 0
	}

	const getChipLabel = () => {
		if (
			props.eventResponse &&
			props.eventResponse.itemEvents &&
			props.eventResponse.itemEvents.length > 0
		) {
			const cancelledItemsCount = getCancelledItemsCount()
			return cancelledItemsCount === props.eventResponse.itemEvents.length
				? "Cancelled"
				: `${cancelledItemsCount} item${cancelledItemsCount > 1 ? "s" : ""} cancelled`
		}
		return ""
	}
	return (
		<Box className={classes.container}>
			<Timeline position="right" className={classes.timeline}>
				{props.orderTimelineDetails.map((timelineItem) => {
					return (
						<TimelineItem className={classes.timelineItem}>
							<TimelineSeparator>
								<TimelineDot
									variant={isStatusHighlighted(timelineItem) ? "filled" : "outlined"}
									className={
										isStatusHighlighted(timelineItem)
											? classes.timelineDotHighlighted
											: classes.timelineDot
									}
								/>
								{orderStatuses.indexOf(timelineItem.status) < orderStatuses.length - 1 && (
									<TimelineConnector
										className={
											isStatusHighlighted(timelineItem)
												? classes.timelineConnectorHighlighted
												: classes.timelineConnector
										}
									/>
								)}
							</TimelineSeparator>
							<Tooltip
								title={
									timelineItem.startDate !== "" ? new Date(timelineItem.startDate).toString() : ""
								}
								disableHoverListener={false}
							>
								<TimelineContent style={{paddingTop: "4px"}}>
									{timelineItem.status === "Order created"
										? displayOrderCreatedStatus()
										: displayOrderTimeLineItem(timelineItem)}
								</TimelineContent>
							</Tooltip>
						</TimelineItem>
					)
				})}
			</Timeline>
			<div className={classes.statusesContainer}>
				<div className={classes.warningsContainer}>
					<WarningIcon className={classes.warningIcon} />
					<Typography className={classes.detail}>
						Warnings: {getWarningsCount(props.eventResponse?.orderEvents || [])}
					</Typography>
				</div>
				<div className={classes.warningsContainer}>
					<InfoIcon className={classes.infoIcon} />
					<Typography className={classes.detail}>
						Updates: {getUpdatesCount(props.eventResponse?.orderEvents || [])}
					</Typography>
				</div>
				{getCancelledItemsCount() > 0 && <Chip label={getChipLabel()} className={classes.chip} />}
			</div>
		</Box>
	)
}
